import type { Ref } from 'vue';
import { watchEffect } from 'vue';
import type { Fn } from '@vueuse/core';
import { useEventListener, useMediaQuery } from '@vueuse/core';

interface TooltipBehavior {
	/** On the parent element of button and tooltip @mouseleave */
	onContainerMouseLeave: (event: Event) => void;
	/** On the parent element of button and tooltip @focusout */
	onContainerFocusOut: (event: FocusEvent) => void;
	/** On the parent element of button and tooltip @mousedown */
	onContainerMouseDown: (event: Event) => void;
	/** On the parent element of button and tooltip @mouseup */
	onContainerMouseUp: (event: Event) => void;
	/** On the parent element of button and tooltip @click */
	onContainerClick: (event: Event) => void;
	/** On the button that opens/closes the tooltip @mousedown */
	onButtonMouseDown: (event: Event) => void;
	/** On the button that opens/closes the tooltip @mouseenter */
	onButtonMouseEnter: (event: Event) => void;
	/** On the button that opens/closes the tooltip @focus */
	onButtonFocus: (event: FocusEvent) => void;
	/** On the button that opens/closes the tooltip @click */
	onButtonClick: (event: Event) => void;
}

/** Duration during which an event is counted as recent. */
const recentEventMs = 50;

/**
 * This composable handles interaction behavior for tooltips.
 * Use the returned event handlers on the associated elements for the
 * associated events.
 */
export const useTooltipBehavior = (showTooltip: Ref<boolean>, container: Ref<HTMLElement | undefined>): TooltipBehavior => {
	const hasFinePointer = useMediaQuery('(pointer: fine)');

	let preventCloseEvent: Event;
	let latestTooltipOpenTimeStamp = -1;
	let latestTooltipCloseTimeStamp = -1;
	let mouseClicking = false;

	const elementInContainer = (el: Element | null) => el && container.value?.contains(el);
	const whenMouse = (fn: () => void) => hasFinePointer.value && fn();
	const whenNotRecentlyClosed = (event: Event, fn: () => void) => event.timeStamp - recentEventMs > latestTooltipCloseTimeStamp && fn();
	const whenNotRecentlyOpened = (event: Event, fn: () => void) => event.timeStamp - recentEventMs > latestTooltipOpenTimeStamp && fn();
	const whenNotMouseClick = (fn: () => void) => !mouseClicking && fn();
	const whenFocusMovedOutside = (event: FocusEvent, fn: () => void) =>
		!!event.relatedTarget && !elementInContainer(event.relatedTarget as Element | null) && fn();
	const whenTooltipNotOpen = (fn: () => void) => !showTooltip.value && fn();

	const openTooltip = (event: Event) => {
		showTooltip.value = true;
		latestTooltipOpenTimeStamp = event.timeStamp;
	};

	const closeTooltip = (event: Event) => {
		if (preventCloseEvent !== event) {
			showTooltip.value = false;
			latestTooltipCloseTimeStamp = event.timeStamp;
			mouseClicking = false;
		}
	};

	const toggleTooltip = (event: Event) => {
		if (showTooltip.value) {
			closeTooltip(event);
		} else {
			openTooltip(event);
		}
	};

	const preventTooltipClose = (event: Event) => {
		preventCloseEvent = event;
	};

	listenersActiveWhen(showTooltip, [
		() => useEventListener(window, 'blur', closeTooltip),
		() => useEventListener(document, 'click', closeTooltip),
		() => useEventListener(document, 'keydown', (event: KeyboardEvent) => event.key === 'Escape' && closeTooltip(event)),
	]);

	return {
		onContainerMouseLeave: (event: Event) => whenMouse(() => closeTooltip(event)),
		onContainerFocusOut: (event: FocusEvent) => whenFocusMovedOutside(event, () => whenNotMouseClick(() => closeTooltip(event))),
		onContainerMouseDown: () => (mouseClicking = true),
		onContainerMouseUp: () => (mouseClicking = false),
		onContainerClick: (event: Event) => preventTooltipClose(event),
		onButtonMouseDown: (event: Event) => event.preventDefault(), // prevents blur event
		onButtonMouseEnter: (event: Event) => whenNotRecentlyClosed(event, () => whenMouse(() => openTooltip(event))),
		onButtonFocus: (event: FocusEvent) => whenTooltipNotOpen(() => openTooltip(event)),
		onButtonClick: (event: Event) => whenNotRecentlyOpened(event, () => toggleTooltip(event)),
	};
};

function listenersActiveWhen(showTooltip: Ref<boolean>, eventListenerFactoryFns: (() => Fn)[]) {
	let cleanupFns: (() => void)[] = [];

	watchEffect((onCleanup) => {
		if (showTooltip.value) {
			cleanupFns = eventListenerFactoryFns.map((fn) => fn());
		} else {
			cleanupFns.forEach((cleanupFn) => cleanupFn());
		}

		onCleanup(() => cleanupFns.forEach((cleanupFn) => cleanupFn()));
	});
}
